import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../css/Progress.css';
import { subscribeToPanelData } from '../../services/PanelDataService';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';

const ProgressTile = ({ title, progress, status, color = '#20C997', path }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        if (path) {
            navigate(path);
        }
    };

    return (
        <div
            className="dashboard-tile"
            onClick={handleClick}
            style={{
                cursor: path ? 'pointer' : 'default',
                transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={(e) => {
                if (path) e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            <div className="tile-header">
                <h5 className="tile-title">{title}</h5>
                <span className="tile-percentage">{progress}%</span>
            </div>
            <div className="progress-bar-container">
                <div
                    className="progress-bar-fill"
                    style={{ width: `${progress}%`, backgroundColor: color }}
                ></div>
            </div>
            <p className="tile-status">{status}</p>
        </div>
    );
};

const FORM_IDS = {
    REGISTRATION: '68c7eefbd44999eedb541043',
    ACCREDITATION: '68e6505866f25479291b933f',
    TRAVEL: '68e781408e1384b2c283c3af',
    ACCOMMODATION: '68e784018e1384b2c283c3bb',
};

const calculateOverallProgress = (tiles) => {
    if (!tiles || tiles.length === 0) return 0;
    const totalTiles = tiles.length;
    const totalProgressSum = tiles.reduce((sum, tile) => sum + tile.progress, 0);
    return Math.round(totalProgressSum / totalTiles);
};

const ProgressOverview = () => {

    const [username, setUsername] = useState("");
    const [dynamicPanelData, setDynamicPanelData] = useState({
        totalPanels: 1,
        completedPanels: 1,
    });

    const [documentStats, setDocumentStats] = useState({
        total: 0,
        signed: 0,
    });

    // Load username
    useEffect(() => {
        const storedName = localStorage.getItem("username");
        if (storedName && storedName !== "undefined" && storedName !== "null") {
            setUsername(storedName);
        }
    }, []);

    // Subscribe to form panel progress
    useEffect(() => {
        const unsubscribe = subscribeToPanelData((data) => {
            setDynamicPanelData(data.personalDetails || { totalPanels: 1, completedPanels: 1 });
        });
        return () => unsubscribe();
    }, []);

    // Fetch document status (same API as Documents page: athlete by personId, DB uses SIGNED / NOT_SIGNED)
    useEffect(() => {
        const fetchDocuments = async () => {
            const personId = localStorage.getItem('personId');
            if (!personId || !API_URL) return;

            try {
                const res = await axios.get(`${API_URL}/esign-documents/athlete/${personId}`);
                const data = res.data;
                const docs = Array.isArray(data) ? data : data ? [data] : [];

                const totalDocs = docs.length;

                const signedDocs = docs.filter((doc) => {
                    const status = (doc.status || '').toString().toUpperCase();
                    return status === 'SIGNED' || status === 'COMPLETED';
                }).length;

                setDocumentStats({
                    total: totalDocs,
                    signed: signedDocs,
                });
            } catch (err) {
                console.error('Error fetching documents:', err);
            }
        };

        fetchDocuments();
    }, []);

    const totalPanels = dynamicPanelData.totalPanels || 1;
    const completedPanels = dynamicPanelData.completedPanels || totalPanels;

    const personalDetailsProgress =
        totalPanels === 0 ? 100 : Math.round((completedPanels / totalPanels) * 100);

    const personalDetailsStatus =
        `${completedPanels} of ${totalPanels} modules completed`;

    // Documents Progress
    const documentProgress =
        documentStats.total === 0
            ? 0
            : Math.round((documentStats.signed / documentStats.total) * 100);

    const documentStatus =
        documentStats.total === 0
            ? 'No documents available'
            : `${documentStats.signed} of ${documentStats.total} Documents signed`;

    const dashboardTiles = [
        {
            title: 'Personal details',
            progress: personalDetailsProgress,
            status: personalDetailsStatus,
            path: `/user/form/view/registration/${FORM_IDS.REGISTRATION}`,
        },
        // DISABLED: { title: 'Tickets', progress: 33, status: '1 of 3 modules completed' },
        {
            title: 'Hotel',
            progress: personalDetailsProgress,
            status: personalDetailsStatus,
            path: `/user/form/view/accommodation/${FORM_IDS.ACCOMMODATION}`,
        },
        {
            title: 'Travel',
            progress: personalDetailsProgress,
            status: personalDetailsStatus,
            path: `/user/form/view/travel/${FORM_IDS.TRAVEL}`,
        },
        {
            title: 'Accreditation',
            progress: personalDetailsProgress,
            status: personalDetailsStatus,
            path: `/user/form/view/accreditation/${FORM_IDS.ACCREDITATION}`,
        },
        { title: 'Messages', progress: 33, status: '1 of 3 messages read' },
        {
            title: 'Documents',
            progress: documentProgress,
            status: documentStatus,
            path: '/user/esign-documents'
        },
        // DISABLED (IGFEMS-508): Change Log tile hidden — hardcoded fake data (33%, "1 of 3 changes read"), no backend/path. Participants don't need change logs. See docs/DISABLED_FEATURES.md
        // { title: 'Change Log', progress: 33, status: '1 of 3 changes read' },
    ];

    const overallProgress = calculateOverallProgress(dashboardTiles);
    const isComplete = overallProgress === 100;
    const progressColor = isComplete ? '#20C997' : '#dbaa41ce';

    return (
        <div className="user-dashboard">
            <div className="dashboard-header">
                <div className="welcome-section">
                    <h1 className="welcome-message">
                        Hello, <span className="user-name" style={{ color: "#D9A128" }}>{username}</span>, welcome back!
                    </h1>
                    <p className="welcome-subtext">Complete your profile to get started.</p>
                </div>
            </div>

            <div className="approach-shot-card">
                <div className="card-content">
                    <div className="icon-and-title">
                        <div className="golf-icon-placeholder">⛳</div>
                        <h3 className="card-title" style={{ color: "#fff" }}>Approach Shot</h3>
                    </div>
                    <p className="card-subtext">
                        What a shot! You're right by the pin. Just a simple tap-in to finish the hole.
                    </p>
                </div>
                <div className="overall-progress">
                    <span className="overall-percentage" style={{ color: progressColor }}>
                        {overallProgress}%
                    </span>
                    <div className="overall-bar-container">
                        <div
                            className="overall-bar-fill"
                            style={{ width: `${overallProgress}%`, backgroundColor: progressColor }}
                        ></div>
                    </div>
                </div>
            </div>

            <div className="progress-grid-container">
                {dashboardTiles.map((tile, index) => (
                    <ProgressTile
                        key={index}
                        title={tile.title}
                        progress={tile.progress}
                        status={tile.status}
                        color={
                            tile.progress === 100
                                ? '#20C997'
                                : tile.progress === 0
                                ? '#DC3545'
                                : '#D9a128'
                        }
                        path={tile.path}
                    />
                ))}
            </div>
        </div>
    );
};

export default ProgressOverview;